import {
	AfterViewInit,
	Component,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	ViewChild,
} from '@angular/core';
import { AnimationItem } from 'lottie-web';
import lottie from 'lottie-web/build/player/lottie_light';

import { AppService } from '~ng-common-lib/services/app.service';

@Component({
	selector: 'lottie-animation',
	standalone: true,
	styleUrl: './lottie-animation.component.scss',
	template: ` <div #container class="animation-container w-full h-full"></div> `,
})
export class LottieAnimationComponent implements AfterViewInit, OnDestroy {
	private readonly appService = inject(AppService);

	@ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;
	public readonly $finished = output({ alias: 'finished' });

	public readonly $path = input.required<string>({ alias: 'path' });
	public readonly $loop = input.required<boolean>({ alias: 'loop' });
	public readonly $autoplay = input.required<boolean>({ alias: 'autoplay' });

	public readonly $staticFrame = input(false, { alias: 'staticFrame' });

	public readonly $useFinishTimeout = input(true, { alias: 'finishTimeout' });
	public readonly $finishDelay = input(750, { alias: 'finishTimer' });

	public readonly $usePlayTimeout = input(true, { alias: 'playTimeout' });
	public readonly $playDelay = input(600, { alias: 'playTimer' });

	public readonly $reverseLoop = input(false, { alias: 'reverseLoop' });

	private anim?: AnimationItem;
	private finishTimeoutId?: any;
	private playTimeoutId?: any;

	get processedPath(): string {
		const src = this.$path();
		if (!src) return '';
		return src.replace(/\/image$/i, '/file');
	}

	ngAfterViewInit(): void {
		if (this.appService.isServer) return;

		this.anim = lottie.loadAnimation({
			container: this.container.nativeElement,
			renderer: 'svg',
			loop: this.$loop(),
			autoplay: this.$autoplay(),
			path: this.processedPath,
			rendererSettings: {
				preserveAspectRatio: 'xMidYMid slice',
			},
		});

		if (this.$staticFrame()) {
			this.anim.addEventListener('DOMLoaded', () => {
				const lastFrame = this.anim?.totalFrames ? this.anim.totalFrames - 1 : 0;
				this.anim?.goToAndStop(lastFrame, true);
				this.$finished.emit();
			});
			return;
		}

		if (this.$usePlayTimeout() && this.$playDelay() && !this.$reverseLoop()) {
			this.playTimeoutId = setTimeout(() => {
				this.anim?.play();
			}, this.$playDelay());
		} else {
			this.anim?.play();
		}

		if (this.$useFinishTimeout() && this.$finishDelay() && !this.$reverseLoop()) {
			this.finishTimeoutId = setTimeout(() => {
				if (!this.anim) return;
				this.$finished.emit();
			}, this.$finishDelay());
		} else {
			this.$finished.emit();
		}

		if (this.$reverseLoop()) {
			let direction: 1 | -1 = 1;

			this.anim.addEventListener('complete', () => {
				direction = -direction as 1 | -1;
				this.anim?.setDirection(direction);

				setTimeout(() => this.anim?.play(), 500);

				if (this.anim) {
					this.$finished.emit();
				}
			});
		}
	}

	ngOnDestroy(): void {
		if (this.playTimeoutId) clearTimeout(this.playTimeoutId);
		if (this.finishTimeoutId) clearTimeout(this.finishTimeoutId);

		this.anim?.destroy();
		this.anim = undefined;
	}
}
